<template>
  <div class="bg-white rounded-lg p-4 shadow">
    <div class="flex flex-col gap-2">
      <h3 class="text-lg font-semibold">{{ $t('settings.support.title') }}</h3>
      <p class="text-sm text-gray-600">{{ $t('settings.support.description') }}</p>
      <div class="flex flex-col gap-1 mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
        <p class="font-medium text-green-600 text-lg">
          {{ $t('settings.support.tollfree') }}:
          <span class="font-bold">0 801 311 827</span>
        </p>
        <p class="text-sm text-gray-600">
          {{ $t('settings.support.or') }} {{ $t('settings.support.international') }}:
          <span class="font-medium">+33939377043</span>
        </p>
      </div>
      <div class="flex flex-col items-center gap-2 mt-2 flex-grow justify-center">
        <SkyButton
          v-if="!supportCode"
          :loading="loading"
          size="small"
          @click="fetchSupportCode"
        >
          {{ $t('settings.support.showButton') }}
        </SkyButton>
        <div
          v-if="supportCode"
          class="flex flex-row items-center gap-2"
        >
          <span class="font-mono font-bold">{{ supportCode }}</span>
          <SkyButton
            size="tiny"
            @click="copySupportCode"
          >
            <FontAwesomeIcon :icon="faCopy" />
          </SkyButton>
        </div>
        <div
          v-if="expiresAt"
          class="flex flex-row items-center gap-2"
        >
          <span class="text-sm text-gray-600">
            {{ $t('settings.support.expiresIn') }}:
            <span class="font-mono font-bold text-amber-600">{{ formattedExpiresAt }}</span>
          </span>
        </div>
      </div>
      <p
        v-if="error"
        class="text-sm text-red-500"
      >
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script>
import { reactive, toRefs, computed } from 'vue';
import { useStore } from 'vuex';
import { useNotify } from '@/plugins/notify/index.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import { useI18n } from 'vue-i18n';
import { DateValueObject } from '@/utils/value-objects/date/date-value-object.js';
import SkyButton from '@/components/ui/buttons/SkyButton.vue';

export default {
  name: 'SupportCodeWidget',
  components: {
    SkyButton,
    FontAwesomeIcon,
  },
  setup() {
    const store = useStore();
    const notify = useNotify();
    const { t } = useI18n();

    const state = reactive({
      loading: false,
      supportCode: null,
      expiresAt: null,
      error: null,
    });

    async function fetchSupportCode() {
      state.loading = true;
      state.error = null;

      try {
        const response = await store.dispatch('apps/fetchSupportCode');
        state.supportCode = response.code;
        state.expiresAt = response.expiresAt;
      } catch (err) {
        state.error = err.message || t('settings.support.error');
        notify.error(state.error);
      } finally {
        state.loading = false;
      }
    }

    function copySupportCode() {
      if (!state.supportCode) return;

      navigator.clipboard
        .writeText(state.supportCode)
        .then(() => {
          notify.success(t('settings.support.copied'));
        })
        .catch(() => {
          notify.error(t('settings.support.copyError'));
        });
    }

    const formattedExpiresAt = computed(() => {
      if (!state.expiresAt) return '';

      try {
        const expiresDate = DateValueObject.fromNative(state.expiresAt);
        return expiresDate.getRelativeTime();
      } catch (err) {
        return state.expiresAt;
      }
    });

    return {
      ...toRefs(state),
      fetchSupportCode,
      copySupportCode,
      faCopy,
      formattedExpiresAt,
    };
  },
};
</script>
